import React, {useMemo, useCallback, useState, useEffect} from "react";
import {Modal, Collapse, ConfigProvider, Form} from "antd";
import moment from "moment";
import classNames from "classnames";
import {compensationOptionsToText, issueOptionsToText, statusOptions, statusOptionsToText} from "./utils.jsx";
import {IssueForm} from "./IssueForm";
import "./issue-modal.css";
import "./order-issue-logs-modal.css";

const {Panel} = Collapse;

const EditableIssueLogPanel = ({
    issueData,
    onSave,
    declineReasons,
    declineReasonLabel,
    formatDate,
    getIssueReasonText,
    getStatusText,
    getCompensationTypeText,
    readonly = false
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (issueData) {
            const compensationTypeValue = issueData.compensationType
                ? typeof issueData.compensationType === "string"
                    ? issueData.compensationType.split(", ").filter(Boolean)
                    : Array.isArray(issueData.compensationType)
                    ? issueData.compensationType
                    : [issueData.compensationType]
                : [];

            form.setFieldsValue({
                issueReason: issueData.issueReason,
                issueComment: issueData.issueComment || "",
                compensationOffered: issueData.compensationOffered || "",
                compensationAmount: issueData.compensationAmount || "",
                compensationType: compensationTypeValue,
                compensationComment: issueData.compensationComment || "",
                status: issueData.status || "",
                shouldShowToProvider: issueData.shouldShowToProvider || false
            });
        }
    }, [issueData, form]);

    const handleSave = useCallback(
        async dataToSave => {
            const finalData = {
                ...dataToSave,
                logId: issueData.logId
            };

            if (!dataToSave.imageUrl && issueData.imageUrl) {
                finalData.imageUrl = issueData.imageUrl;
            }

            await onSave(finalData);
        },
        [onSave, issueData.logId, issueData.imageUrl]
    );

    return (
        <div className="order-issue-log-edit-content">
            <div className="order-issue-log-meta">
                {issueData.editor && (
                    <div className="order-issue-log-meta-item">
                        <strong>נרשם על ידי:</strong> {issueData.editor.name || issueData.editor.email || "לא זמין"}
                    </div>
                )}
                <div className="order-issue-log-meta-item">
                    <strong>תאריך:</strong> {formatDate(issueData.updatedAt ?? issueData.createdAt)}
                </div>
                {issueData.status && (
                    <div className="order-issue-log-meta-item">
                        <strong>סטטוס:</strong> {getStatusText(issueData.status)}
                    </div>
                )}
            </div>
            {issueData.imageUrl && (
                <div className="order-issue-log-existing-image">
                    <strong>תמונה קיימת:</strong>
                    <div className="order-issue-log-image">
                        <img src={issueData.imageUrl} alt="Existing issue" />
                    </div>
                </div>
            )}
            {(issueData.compensationType || issueData.compensationComment) && (
                <div className="order-issue-log-compensation-info">
                    {issueData.compensationType && (
                        <div className="order-issue-log-field">
                            <strong>סוג פיצוי:</strong>
                            <div>{getCompensationTypeText(issueData.compensationType.split(", "))}</div>
                        </div>
                    )}
                    {issueData.issueComment && (
                        <div className="order-issue-log-field">
                            <strong>הסבר תקלה:</strong>
                            <div>{issueData.issueComment}</div>
                        </div>
                    )}
                </div>
            )}
            {!readonly && (
                <Collapse className="order-issue-log-edit-collapse">
                    <Panel header="ערוך תקלה" key="edit">
                        <IssueForm
                            form={form}
                            onSave={handleSave}
                            onCancel={() => {}}
                            declineReasons={declineReasons}
                            declineReasonLabel={declineReasonLabel}
                            showProviderCheckbox={false}
                        />
                    </Panel>
                </Collapse>
            )}
        </div>
    );
};

export const OrderIssueLogsModal = ({
    visible,
    onClose,
    onSave,
    order,
    declineReasons = null,
    header = "תקלות בהזמנה",
    declineReasonLabel = "בחרו את סוג התקלה",
    readonly = false
}) => {
    const [form] = Form.useForm();
    const [activeKeys, setActiveKeys] = useState([]);

    const sortedLogs = useMemo(() => {
        if (!order?.orderIssueLogs?.length) {
            return [];
        }
        return [...order.orderIssueLogs].sort((a, b) => {
            const aTime = a.createdAt || 0;
            const bTime = b.createdAt || 0;
            return bTime - aTime;
        });
    }, [order?.orderIssueLogs]);

    const extractIssueData = useCallback(log => {
        if (!log?.changedFields?.length) {
            return null;
        }

        const issueData = {};
        log.changedFields.forEach(field => {
            switch (field.fieldId) {
                case "issue":
                    issueData.issueReason = field.newValue;
                    break;
                case "issueComment":
                    issueData.issueComment = field.newValue;
                    break;
                case "issueImage":
                    issueData.imageUrl = field.newValue;
                    break;
                case "compensationOffered":
                    issueData.compensationOffered = field.newValue;
                    break;
                case "compensationAmount":
                    issueData.compensationAmount = field.newValue;
                    break;
                case "compensationType":
                    issueData.compensationType = field.newValue;
                    break;
                case "compensationComment":
                    issueData.compensationComment = field.newValue;
                    break;
                case "status":
                    issueData.status = field.newValue;
                    break;
                case "shouldShowToProvider":
                    issueData.shouldShowToProvider = field.newValue;
                    break;
            }
        });

        if (!issueData.issueReason) {
            return null;
        }

        return {
            ...issueData,
            issueReason: issueData.issueReason,
            status: issueData.status,
            createdAt: log.createdAt,
            updatedAt: log.updatedAt,
            editor: log.editor,
            logId: log.logId
        };
    }, []);

    const issueLogsWithData = useMemo(() => {
        return sortedLogs.map(log => extractIssueData(log)).filter(Boolean);
    }, [sortedLogs, extractIssueData]);

    const handleClose = useCallback(() => {
        form.resetFields();
        onClose();
    }, [onClose, form]);

    const handleSave = useCallback(
        async dataToSave => {
            await onSave(dataToSave);
            form.resetFields();
        },
        [onSave, form]
    );

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setActiveKeys([]);
        }
    }, [visible, form]);

    const formatDate = timestamp => {
        if (!timestamp) return "תאריך לא זמין";
        return moment(timestamp).format("DD/MM/YYYY HH:mm");
    };

    const getIssueReasonText = issueReason => {
        if (!issueReason) return "לא צוין";
        return issueOptionsToText[issueReason] || declineReasons?.[issueReason] || issueReason;
    };

    const getStatusText = status => {
        return statusOptionsToText[status] || status;
    };

    const getCompensationTypeText = type => {
        return Array.isArray(type)
            ? type.map(t => compensationOptionsToText[t] || t).join(", ")
            : compensationOptionsToText[type] || type;
    };

    return (
        <ConfigProvider direction="rtl">
            <Modal
                title={header}
                open={visible}
                onCancel={onClose}
                footer={null}
                width={600}
                className="order-issue-logs-modal">
                <div className="order-issue-logs-content">
                    {issueLogsWithData.length > 0 ? (
                        <div className="order-issue-logs-list">
                            <h3 className="order-issue-logs-sub-title">תקלות קיימות ({issueLogsWithData.length})</h3>
                            <Collapse
                                accordion={true}
                                activeKey={activeKeys}
                                onChange={key => setActiveKeys(Array.isArray(key) ? key : [key])}
                                className="order-issue-logs-collapse">
                                {issueLogsWithData.map((issueData, index) => (
                                    <Panel
                                        header={
                                            <div className="order-issue-log-header">
                                                <div className="order-issue-log-header-left">
                                                    <span className="order-issue-log-reason">
                                                        {getIssueReasonText(issueData.issueReason)}
                                                    </span>
                                                    {issueData.status && (
                                                        <span
                                                            className={classNames("order-issue-log-status", {
                                                                "order-issue-log-status-closed":
                                                                    issueData.status ===
                                                                    statusOptions.closedWithCustomer
                                                            })}>
                                                            {getStatusText(issueData.status)}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="order-issue-log-date">
                                                    {formatDate(issueData.updatedAt ?? issueData.createdAt)}
                                                </span>
                                            </div>
                                        }
                                        key={issueData.logId || index.toString()}
                                        className="order-issue-log-panel">
                                        <EditableIssueLogPanel
                                            issueData={issueData}
                                            onSave={handleSave}
                                            declineReasons={declineReasons}
                                            declineReasonLabel={declineReasonLabel}
                                            formatDate={formatDate}
                                            getIssueReasonText={getIssueReasonText}
                                            getStatusText={getStatusText}
                                            getCompensationTypeText={getCompensationTypeText}
                                            readonly={readonly}
                                        />
                                    </Panel>
                                ))}
                            </Collapse>
                        </div>
                    ) : (
                        <div className="order-issue-logs-empty">
                            <p>אין תקלות רשומות להזמנה זו</p>
                        </div>
                    )}

                    {!readonly && (
                        <div className="order-issue-logs-add-new">
                            <h3 className="order-issue-logs-title">הוספת תקלה</h3>
                            <IssueForm
                                form={form}
                                onSave={handleSave}
                                onCancel={handleClose}
                                declineReasons={declineReasons}
                                declineReasonLabel={declineReasonLabel}
                                showProviderCheckbox={true}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </ConfigProvider>
    );
};
